import { useSyncExternalStore } from 'react'
import { useQuery } from 'react-query'
import { fetchMidasConfig, MidasConfig } from 'api/midas'
import { useAuth } from 'components/AuthProvider'
import { useClientConfig } from 'hooks/useClientConfig'

export interface MidasAvailability {
  enabled: boolean
  config?: MidasConfig
}

// Midas shows only when the `midas_enabled` flag is on AND GET /midas/config
// says enabled (the admin kill-switch). Absent, loading or errored all mean OFF,
// so the existing Help Centre link stays until both answers are in.
export function useMidasEnabled(): MidasAvailability {
  const { user } = useAuth()
  const { data: clientConfig } = useClientConfig()
  const flagOn = !!user && clientConfig?.features?.midas_enabled === true

  // Keyed by customer: the welcome can greet them by first name, and signing out
  // does not clear the query cache, so the next customer on the same tab must
  // not be shown the last one's.
  const { data: config } = useQuery(
    ['midas', 'config', user?.id],
    fetchMidasConfig,
    { enabled: flagOn, staleTime: 5 * 60_000, retry: false }
  )

  const enabled = flagOn && config?.enabled === true
  return { enabled, config: enabled ? config : undefined }
}

// The panel's open state lives outside React so it survives page navigation
// (each page renders its own layout, which remounts the launcher).
let panelOpen = false
const listeners = new Set<() => void>()

function setPanelOpen(open: boolean): void {
  if (panelOpen === open) return
  panelOpen = open
  listeners.forEach(listener => listener())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export const openMidasPanel = (): void => setPanelOpen(true)
export const closeMidasPanel = (): void => setPanelOpen(false)

export function useMidasPanelOpen(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => panelOpen,
    () => false
  )
}
